import {
    getDay,
    parse,
    format,
    startOfWeek,
} from 'date-fns';
import { de, enUS } from 'date-fns/locale';
import {
    Calendar,
    dateFnsLocalizer,
} from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import {
    Box,
    Typography,
} from '@mui/material';
import CalendarColors from "./CalendarColors";

import TimeKeepingCalendarEvent from "./TimeKeepingCalendarEvent";
import TimeKeeperCalendarToolbar from "./TimeKeepingCalendarToolbar";

import i18n from "../../../i18n";
import DateFormat from "../../../utils/DateFormat";
import { useTranslation } from "react-i18next";
import TimeKeeperCalendarFooter from "./TimeKeeperCalendarFooter";
import { workLoadForDay } from "../../../model/contractInfo";

enUS.options.weekStartsOn = 1 // override Week start day in US locale

const locales = {
    'de': de,
    'en': enUS,
};

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

const MonthDateCellWrapper = (props) => {
    const { t } = useTranslation()
    return (
        <Box
            sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            {...props.children.props}
        >
            <Typography sx={{ fontWeight: 'bold' }} variant="subtitle2">
                {props.children.props.style?.type === 'closingDay' ?
                    t('brückentag', { ns: 'timeKeepingPage', defaultValue: 'Brückentag' })

                    : props.children.props.style?.type === 'holiday' ?
                        t('feiertag', { ns: 'timeKeepingPage', defaultValue: 'Feiertag' })
                        : ''
                }
            </Typography>
        </Box>
    );
};

const DayDateCellWrapper = (props) => {
    return (
        <Box
            sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            {...props.children.props}
        >
        </Box>
    );
};


// Function for the function (dayPropGetter in Calendar) for day coloring coment
const getDayPropGetter = (holidays, closingDays, contractInfo, view, workDays, startOfFlexTimeAccount) => {

    const dayPropGetter = (date) => {
        let s = DateFormat.formatISOStringWithTimeZone(date).split('T')[0];

        let isClosingDay = closingDays.filter((cd) => {
            let s2 = DateFormat.formatISOStringWithTimeZone(cd).split('T')[0];
            return s2 === s;
        }).length > 0;

        let isHoliday = holidays.filter((hd) => {
            let s2 = DateFormat.formatISOStringWithTimeZone(hd).split('T')[0];
            return s2 === s
        }).length > 0;

        let day = workDays.find(wd => {
            let d1 = DateFormat.formatISOStringWithTimeZone(wd.date).split('T')[0]
            let d2 = DateFormat.formatISOStringWithTimeZone(date).split('T')[0]
            return d1 === d2
        })


        if (isClosingDay) {
            return {
                style: {
                    backgroundColor: CalendarColors.get('closingDay'),
                    type: "closingDay",

                },
            };
        };

        if (isHoliday) {
            return {
                style: {
                    backgroundColor: CalendarColors.get('holiday'),
                    type: "holiday",
                },
            };
        };

        if (view === 'week') {

            if (contractInfo) {
                if (day && day.date.getTime() > startOfFlexTimeAccount.getTime()) {
                    if (!day.contractWorkLoad) {
                        return {
                            style: {
                                backgroundColor: CalendarColors.get('offWork'),
                                type: "",
                            },
                        };
                    }
                }

                if (workLoadForDay(contractInfo, date.getDay()) === 0) {
                    return {
                        style: {
                            backgroundColor: CalendarColors.get('offWork'),
                            type: "",
                        },
                    };
                };

            } else {
                if (date.getDay() % 6 === 0) {
                    return {
                        style: {
                            backgroundColor: CalendarColors.get('offWork'),
                            type: "",
                        },
                    };
                };
            };
        }
    };

    return dayPropGetter;
};

const slotPropGetter = (date) => {
    return {
        style: {
            border: 'none'
        },
    };
};

const eventPropGetter = (event, start, end, isSelected) => {
    let baseStyle = {
        color: 'black',
        borderRadius: "0px",
        border: 'none',
    };

    switch (event.type) {
        case 'WORK_ENTRY':
            return {
                style: {
                    ...baseStyle,
                    backgroundColor: CalendarColors.get('workEntry'),
                    color: 'white',
                    overflow: 'visible'
                },
            };
        case 'WORKDAY':
            return {
                style: {
                    ...baseStyle,
                    backgroundColor: CalendarColors.get('workDay'),
                },
            };
        case 'DAY_SUMMARY':
            return {
                style: {
                    ...baseStyle,
                    backgroundColor : 'white',
                    height : "100%",
                    cursor : 'default'
                },
            }
        default:
            break;
    };
};

function TimeKeepingCalendar({
    workDays,
    workEntries,
    date,
    location,
    contractInfo,
    onSelectEvent,
    onSelectSlot,
    onNavigate,
    legend,
    views,
    view,
    currentFlexTimeAccount,
    startOfFlexTimeAccount
}) {
    return (
        <>
            <Calendar
                localizer={localizer}
                culture={i18n.language}
                defaultDate={date}
                slotPropGetter={slotPropGetter}
                date={date}
                onNavigate={onNavigate}
                defaultView={view}
                views={views}
                showMultiDayTimes
                events={workEntries}
                backgroundEvents={workDays}
                eventPropGetter={eventPropGetter}
                dayPropGetter={getDayPropGetter(location ? location.holidays : [], location ? location.closingDays : [], contractInfo, view, workDays, startOfFlexTimeAccount)}
                onSelectEvent={onSelectEvent}
                scrollToTime={new Date(new Date().setHours(7, 30, 0, 0))}
                onSelectSlot={onSelectSlot}
                components={{
                    event: TimeKeepingCalendarEvent,
                    toolbar: TimeKeeperCalendarToolbar(view),
                    dateCellWrapper: view === 'month' ? MonthDateCellWrapper : DayDateCellWrapper,
                }}
                style={{ height: '700px' }}
                selectable
                resizable
            />
            <TimeKeeperCalendarFooter
                legend={legend}
                currentFlexTimeAccount={contractInfo ? currentFlexTimeAccount : null}
            />
        </>
    );
};

export default TimeKeepingCalendar
